"use client";

import type { ContributionType } from "@/data/contribution-types";
import type { ContributionTypeStats } from "@/util/contributions";
import { ContributionTypeBadge } from "./contribution-type-badge";
import { FilterChip } from "./ui/filter-chip";

/**
 * Visual breakdown of contribution types as a horizontal bar chart
 */
export function ContributionTypeBreakdown({
  stats,
  activeType,
  onTypeClick,
}: {
  stats: ContributionTypeStats[];
  activeType?: ContributionType | null;
  onTypeClick?: (type: ContributionType | null) => void;
}) {
  // Filter to only show types with contributions
  const activeStats = stats.filter((stat) => stat.count > 0);

  if (activeStats.length === 0) {
    return null;
  }

  const handlePressedChange = (type: ContributionType, pressed: boolean) => {
    if (!onTypeClick) return;
    // Toggle on/off based on pressed state
    onTypeClick(pressed ? type : null);
  };

  return (
    <section data-testid="contribution-type-breakdown" className="space-y-4">
      <div className="space-y-1.5">
        <div className="text-lg/9 font-semibold tracking-tight">
          Contribution Types
        </div>
      </div>
      <div className="grid min-w-0 grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {activeStats.map((stat) => {
          const isActive = activeType === stat.type;
          const isClickable = Boolean(onTypeClick);

          // Stable key - no remount hack needed with controlled state
          return (
            <FilterChip
              key={stat.type}
              pressed={isActive}
              onPressedChange={(pressed) =>
                handlePressedChange(stat.type, pressed)
              }
              isClickable={isClickable}
              data-testid="contribution-type-chip"
            >
              <div className="shrink-0">
                <ContributionTypeBadge type={stat.type} />
              </div>
              <div className="flex min-w-0 items-center gap-2">
                <div className="dark:bg-muted dark:group-data-[state=on]:bg-muted h-2 flex-1 overflow-hidden rounded-full bg-[rgba(0,0,0,0.08)] group-data-[state=on]:bg-[rgba(0,0,0,0.08)]">
                  <div
                    className="bg-brand/50 group-data-[state=on]:bg-brand h-full rounded-full transition-all duration-300 motion-reduce:transition-none"
                    style={{ width: `${stat.percentage}%` }}
                    role="progressbar"
                    aria-valuenow={stat.percentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${stat.type}: ${stat.percentage.toFixed(0)}%`}
                  />
                </div>
                <span className="text-muted-foreground w-20 shrink-0 text-right text-base tabular-nums">
                  {stat.count} ({stat.percentage.toFixed(0)}%)
                </span>
              </div>
            </FilterChip>
          );
        })}
      </div>
    </section>
  );
}
